import React, { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  TrendingUp,
  DollarSign,
  Users,
  Globe,
  Target,
  BarChart3,
  PieChart,
  Zap,
  Shield,
  Heart,
  Camera,
  Database,
  Smartphone,
  Building,
  Award,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Calendar,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";

const BusinessPlanPage = () => {
  const { themeClasses } = useTheme();
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", name: "Executive Summary", icon: TrendingUp },
    { id: "market", name: "Market Opportunity", icon: Globe },
    { id: "product", name: "Product & Technology", icon: Zap },
    { id: "business-model", name: "Business Model", icon: DollarSign },
    { id: "revenue", name: "Revenue Projections", icon: BarChart3 },
    { id: "competition", name: "Competitive Analysis", icon: Target },
    { id: "marketing", name: "Go-to-Market Strategy", icon: Users },
    { id: "financials", name: "Financial Projections", icon: PieChart },
    { id: "team", name: "Team & Operations", icon: Building },
    { id: "investment", name: "Investment Opportunity", icon: Award },
  ];

  const renderMetricCard = (title, value, subtitle, icon, color) => (
    <div
      className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
    >
      <div className="flex items-center justify-between mb-4">
        {React.createElement(icon, { className: `w-8 h-8 ${color}` })}
        <span className={`text-2xl font-bold ${themeClasses.text.primary}`}>
          {value}
        </span>
      </div>
      <h3 className={`font-semibold mb-2 ${themeClasses.text.primary}`}>
        {title}
      </h3>
      <p className={`text-sm ${themeClasses.text.secondary}`}>{subtitle}</p>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="space-y-8">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-3xl font-bold mb-6 ${themeClasses.text.primary}`}
              >
                PetID.Neural: Executive Summary
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    The Opportunity
                  </h3>
                  <p className={`mb-4 ${themeClasses.text.secondary}`}>
                    The global pet industry is worth{" "}
                    <strong>$261 billion</strong> and growing at 6.1% annually.
                    With over 900 million dogs worldwide and 70% of US
                    households owning pets, there's a massive untapped market
                    for AI-powered pet identification and health management
                    solutions.
                  </p>
                  <p className={`mb-4 ${themeClasses.text.secondary}`}>
                    Current solutions are fragmented, expensive, and lack AI
                    integration. PetID.Neural revolutionizes pet care with
                    cutting-edge computer vision, comprehensive health tracking,
                    and seamless owner-veterinarian connectivity.
                  </p>
                </div>
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Our Solution
                  </h3>
                  <ul className="space-y-2">
                    <li
                      className={`flex items-center ${themeClasses.text.secondary}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      AI-powered pet identification with 95%+ accuracy
                    </li>
                    <li
                      className={`flex items-center ${themeClasses.text.secondary}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      Comprehensive health record management
                    </li>
                    <li
                      className={`flex items-center ${themeClasses.text.secondary}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      Enterprise API for veterinarians and pet services
                    </li>
                    <li
                      className={`flex items-center ${themeClasses.text.secondary}`}
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                      Scalable SaaS platform with multiple revenue streams
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {renderMetricCard(
                "Market Size",
                "$261B",
                "Global pet industry value",
                Globe,
                "text-blue-400"
              )}
              {renderMetricCard(
                "Target Market",
                "900M+",
                "Dogs worldwide",
                Heart,
                "text-green-400"
              )}
              {renderMetricCard(
                "Revenue Target",
                "$50M",
                "Year 5 projection",
                DollarSign,
                "text-yellow-400"
              )}
              {renderMetricCard(
                "Funding Need",
                "$5M",
                "Series A round",
                TrendingUp,
                "text-purple-400"
              )}
            </div>
          </div>
        );

      case "market":
        return (
          <div className="space-y-8">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-3xl font-bold mb-6 ${themeClasses.text.primary}`}
              >
                Market Opportunity Analysis
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Total Addressable Market (TAM)
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-blue-500/10 rounded-lg">
                      <span className={themeClasses.text.secondary}>
                        Global Pet Care Market
                      </span>
                      <span
                        className={`font-bold ${themeClasses.text.primary}`}
                      >
                        $261B
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-500/10 rounded-lg">
                      <span className={themeClasses.text.secondary}>
                        Pet Tech & Services
                      </span>
                      <span
                        className={`font-bold ${themeClasses.text.primary}`}
                      >
                        $24B
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-purple-500/10 rounded-lg">
                      <span className={themeClasses.text.secondary}>
                        Veterinary Software
                      </span>
                      <span
                        className={`font-bold ${themeClasses.text.primary}`}
                      >
                        $2.1B
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Market Drivers
                  </h3>
                  <ul className="space-y-3">
                    <li
                      className={`flex items-start ${themeClasses.text.secondary}`}
                    >
                      <TrendingUp className="w-5 h-5 text-green-400 mr-3 mt-0.5" />
                      <div>
                        <strong>Growing Pet Ownership:</strong> 70% of US
                        households own pets, up from 56% in 1988
                      </div>
                    </li>
                    <li
                      className={`flex items-start ${themeClasses.text.secondary}`}
                    >
                      <Smartphone className="w-5 h-5 text-blue-400 mr-3 mt-0.5" />
                      <div>
                        <strong>Digital Adoption:</strong> 89% of pet owners use
                        smartphones for pet-related activities
                      </div>
                    </li>
                    <li
                      className={`flex items-start ${themeClasses.text.secondary}`}
                    >
                      <DollarSign className="w-5 h-5 text-yellow-400 mr-3 mt-0.5" />
                      <div>
                        <strong>Increased Spending:</strong> Average annual pet
                        spending: $1,480 per household
                      </div>
                    </li>
                    <li
                      className={`flex items-start ${themeClasses.text.secondary}`}
                    >
                      <Zap className="w-5 h-5 text-purple-400 mr-3 mt-0.5" />
                      <div>
                        <strong>AI Adoption:</strong> 73% of veterinarians
                        interested in AI-powered tools
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg">
                  <Globe className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                  <h4
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Global Reach
                  </h4>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Expanding to 50+ countries with localized solutions
                  </p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg">
                  <Users className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h4
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Target Users
                  </h4>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Pet owners, veterinarians, shelters, and pet service
                    providers
                  </p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg">
                  <TrendingUp className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                  <h4
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Growth Rate
                  </h4>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Pet tech market growing at 22% CAGR through 2028
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "business-model":
        return (
          <div className="space-y-8">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-3xl font-bold mb-6 ${themeClasses.text.primary}`}
              >
                Revenue Streams & Business Model
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Primary Revenue Streams
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 border border-green-500/30 bg-green-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="w-5 h-5 text-green-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Freemium SaaS (40% of revenue)
                        </h4>
                      </div>
                      <p
                        className={`text-sm ${themeClasses.text.secondary} mb-2`}
                      >
                        Free basic pet profiles, premium features at $9.99/month
                      </p>
                      <ul
                        className={`text-xs ${themeClasses.text.secondary} space-y-1`}
                      >
                        <li>• Advanced health tracking</li>
                        <li>• Unlimited photo storage</li>
                        <li>• Veterinarian integration</li>
                        <li>• Priority support</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-blue-500/30 bg-blue-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Building className="w-5 h-5 text-blue-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Enterprise API (35% of revenue)
                        </h4>
                      </div>
                      <p
                        className={`text-sm ${themeClasses.text.secondary} mb-2`}
                      >
                        B2B API licensing to veterinary clinics and pet service
                        providers
                      </p>
                      <ul
                        className={`text-xs ${themeClasses.text.secondary} space-y-1`}
                      >
                        <li>• $0.10 per API call</li>
                        <li>• Volume discounts available</li>
                        <li>• White-label solutions</li>
                        <li>• Custom integrations</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-purple-500/30 bg-purple-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Heart className="w-5 h-5 text-purple-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Pet Insurance Integration (15% of revenue)
                        </h4>
                      </div>
                      <p
                        className={`text-sm ${themeClasses.text.secondary} mb-2`}
                      >
                        Commission from insurance partnerships and referrals
                      </p>
                      <ul
                        className={`text-xs ${themeClasses.text.secondary} space-y-1`}
                      >
                        <li>• 10-15% commission per policy</li>
                        <li>• Risk assessment data licensing</li>
                        <li>• Claims processing automation</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-yellow-500/30 bg-yellow-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Database className="w-5 h-5 text-yellow-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Data & Analytics (10% of revenue)
                        </h4>
                      </div>
                      <p
                        className={`text-sm ${themeClasses.text.secondary} mb-2`}
                      >
                        Anonymized pet health insights for research and product
                        development
                      </p>
                      <ul
                        className={`text-xs ${themeClasses.text.secondary} space-y-1`}
                      >
                        <li>• Pharmaceutical companies</li>
                        <li>• Pet food manufacturers</li>
                        <li>• Academic research institutions</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Pricing Strategy
                  </h3>
                  <div className="space-y-4">
                    <div
                      className={`p-4 ${themeClasses.bg.secondary} rounded-lg`}
                    >
                      <h4
                        className={`font-semibold mb-2 ${themeClasses.text.primary}`}
                      >
                        Consumer Tiers
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Free Plan
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $0/month
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Premium Plan
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $9.99/month
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Family Plan (5 pets)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $19.99/month
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 ${themeClasses.bg.secondary} rounded-lg`}
                    >
                      <h4
                        className={`font-semibold mb-2 ${themeClasses.text.primary}`}
                      >
                        Enterprise Tiers
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Starter (1K calls/month)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $99/month
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Professional (10K calls/month)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $499/month
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Enterprise (Custom)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            Contact Sales
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 border border-cyan-500/30 bg-cyan-500/10 rounded-lg`}
                    >
                      <h4
                        className={`font-semibold mb-2 ${themeClasses.text.primary}`}
                      >
                        Key Metrics
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span
                            className={`block ${themeClasses.text.secondary}`}
                          >
                            Customer LTV
                          </span>
                          <span
                            className={`font-bold ${themeClasses.text.primary}`}
                          >
                            $480
                          </span>
                        </div>
                        <div>
                          <span
                            className={`block ${themeClasses.text.secondary}`}
                          >
                            CAC
                          </span>
                          <span
                            className={`font-bold ${themeClasses.text.primary}`}
                          >
                            $45
                          </span>
                        </div>
                        <div>
                          <span
                            className={`block ${themeClasses.text.secondary}`}
                          >
                            LTV/CAC Ratio
                          </span>
                          <span
                            className={`font-bold ${themeClasses.text.primary}`}
                          >
                            10.7:1
                          </span>
                        </div>
                        <div>
                          <span
                            className={`block ${themeClasses.text.secondary}`}
                          >
                            Churn Rate
                          </span>
                          <span
                            className={`font-bold ${themeClasses.text.primary}`}
                          >
                            3.2%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "revenue":
        return (
          <div className="space-y-8">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-3xl font-bold mb-6 ${themeClasses.text.primary}`}
              >
                5-Year Revenue Projections
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Revenue Growth Trajectory
                  </h3>
                  <div className="space-y-4">
                    {[
                      {
                        year: "Year 1 (2024)",
                        revenue: "$500K",
                        users: "10K",
                        growth: "Launch",
                      },
                      {
                        year: "Year 2 (2025)",
                        revenue: "$2.5M",
                        users: "50K",
                        growth: "400%",
                      },
                      {
                        year: "Year 3 (2026)",
                        revenue: "$8M",
                        users: "150K",
                        growth: "220%",
                      },
                      {
                        year: "Year 4 (2027)",
                        revenue: "$22M",
                        users: "400K",
                        growth: "175%",
                      },
                      {
                        year: "Year 5 (2028)",
                        revenue: "$50M",
                        users: "800K",
                        growth: "127%",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className={`p-4 ${themeClasses.bg.secondary} rounded-lg`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            {item.year}
                          </span>
                          <span className={`text-lg font-bold text-green-400`}>
                            {item.revenue}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className={themeClasses.text.secondary}>
                            {item.users} active users
                          </span>
                          <span className={`text-green-400`}>
                            {item.growth} growth
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Revenue Breakdown by Year 5
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Consumer Subscriptions
                        </span>
                        <span className="text-green-400 font-bold">
                          $20M (40%)
                        </span>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        400K premium subscribers at $50 average annual revenue
                      </p>
                    </div>

                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Enterprise API
                        </span>
                        <span className="text-blue-400 font-bold">
                          $17.5M (35%)
                        </span>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        2,500 enterprise clients with 175M API calls annually
                      </p>
                    </div>

                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Insurance Partnerships
                        </span>
                        <span className="text-purple-400 font-bold">
                          $7.5M (15%)
                        </span>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        50K insurance policies with 12% average commission
                      </p>
                    </div>

                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Data & Analytics
                        </span>
                        <span className="text-yellow-400 font-bold">
                          $5M (10%)
                        </span>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        Anonymized insights to 25 pharmaceutical and research
                        partners
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {renderMetricCard(
                  "ARR by Year 5",
                  "$50M",
                  "Annual recurring revenue",
                  TrendingUp,
                  "text-green-400"
                )}
                {renderMetricCard(
                  "Gross Margin",
                  "85%",
                  "High-margin SaaS business",
                  BarChart3,
                  "text-blue-400"
                )}
                {renderMetricCard(
                  "Market Share",
                  "2.4%",
                  "Of addressable market",
                  Target,
                  "text-purple-400"
                )}
                {renderMetricCard(
                  "Valuation",
                  "$500M",
                  "Projected at 10x revenue",
                  Award,
                  "text-yellow-400"
                )}
              </div>
            </div>
          </div>
        );

      case "investment":
        return (
          <div className="space-y-8">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-3xl font-bold mb-6 ${themeClasses.text.primary}`}
              >
                Investment Opportunity
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Funding Requirements
                  </h3>
                  <div className="space-y-4">
                    <div className="p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg">
                      <h4
                        className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
                      >
                        $5M Series A
                      </h4>
                      <p className={`mb-4 ${themeClasses.text.secondary}`}>
                        Seeking strategic investors to accelerate growth and
                        market expansion
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Equity Offered
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            20-25%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Pre-money Valuation
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $20M
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={themeClasses.text.secondary}>
                            Use of Funds
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            18 months runway
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4
                        className={`font-semibold ${themeClasses.text.primary}`}
                      >
                        Fund Allocation
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-green-500/10 rounded">
                          <span className={themeClasses.text.secondary}>
                            Product Development (40%)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $2M
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded">
                          <span className={themeClasses.text.secondary}>
                            Marketing & Sales (30%)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $1.5M
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded">
                          <span className={themeClasses.text.secondary}>
                            Team Expansion (20%)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $1M
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded">
                          <span className={themeClasses.text.secondary}>
                            Operations & Legal (10%)
                          </span>
                          <span
                            className={`font-semibold ${themeClasses.text.primary}`}
                          >
                            $500K
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3
                    className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                  >
                    Investment Highlights
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 border border-green-500/30 bg-green-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="w-5 h-5 text-green-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Massive Market
                        </h4>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        $261B pet industry with 22% CAGR in pet tech segment
                      </p>
                    </div>

                    <div className="p-4 border border-blue-500/30 bg-blue-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Zap className="w-5 h-5 text-blue-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Proven Technology
                        </h4>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        95%+ accuracy in pet identification with proprietary AI
                        models
                      </p>
                    </div>

                    <div className="p-4 border border-purple-500/30 bg-purple-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="w-5 h-5 text-purple-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Strong Unit Economics
                        </h4>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        10.7:1 LTV/CAC ratio with 85% gross margins
                      </p>
                    </div>

                    <div className="p-4 border border-yellow-500/30 bg-yellow-500/10 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Award className="w-5 h-5 text-yellow-400 mr-2" />
                        <h4
                          className={`font-semibold ${themeClasses.text.primary}`}
                        >
                          Multiple Exit Paths
                        </h4>
                      </div>
                      <p className={`text-sm ${themeClasses.text.secondary}`}>
                        Strategic acquisition by pet care giants or IPO
                        potential
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg">
                    <h4
                      className={`font-semibold mb-2 ${themeClasses.text.primary}`}
                    >
                      Projected Returns
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span
                          className={`block ${themeClasses.text.secondary}`}
                        >
                          3-Year IRR
                        </span>
                        <span className={`text-xl font-bold text-green-400`}>
                          45%
                        </span>
                      </div>
                      <div>
                        <span
                          className={`block ${themeClasses.text.secondary}`}
                        >
                          5-Year Multiple
                        </span>
                        <span className={`text-xl font-bold text-green-400`}>
                          10x
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`p-6 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-lg`}
              >
                <h3
                  className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                >
                  Ready to Invest?
                </h3>
                <p className={`mb-4 ${themeClasses.text.secondary}`}>
                  Join us in revolutionizing pet care with AI-powered solutions.
                  Contact our team to discuss investment opportunities.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-blue-400 mr-2" />
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      investors@petid.neural
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-green-400 mr-2" />
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      +1 (555) 123-4567
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-purple-400 mr-2" />
                    <span className={`text-sm ${themeClasses.text.secondary}`}>
                      Schedule a pitch meeting
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div
            className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
          >
            <h2
              className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
            >
              Section Coming Soon
            </h2>
            <p className={themeClasses.text.secondary}>
              This section is currently being developed. Please check back soon
              for detailed information.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-4xl font-bold mb-2 ${themeClasses.text.primary}`}
          >
            PetID.Neural Business Plan
          </h1>
          <p className={`text-lg ${themeClasses.text.secondary}`}>
            Comprehensive business strategy and investment opportunity
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-80">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary} sticky top-6`}
            >
              <h3 className={`font-semibold mb-4 ${themeClasses.text.primary}`}>
                Business Plan Sections
              </h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? `${themeClasses.bg.button} text-white`
                        : `${themeClasses.cardHover} ${themeClasses.text.secondary}`
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{section.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">{renderSection()}</div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanPage;
